import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, getLogoutRedirectUri } from "@/msalConfig";
import { b2cPolicies } from "@/b2cPolicies";
import store from "@/store";
import * as broadcast from "@/plugins/broadcastChannel";
import * as auth from "@/plugins/auth";

export const msalInstance = new PublicClientApplication(msalConfig);

export function handleAuthentication() {

  return msalInstance
    .handleRedirectPromise()
    .then((response) => {
      if (response) {
        return handleAuthenticatedUser(response);
      }
      return Promise.resolve();
    })
    .catch((error) => {
      console.error("Authentication error:", error);
      return Promise.reject(error);
    });
}

export function logout() {
  store.dispatch("auth/logout");
  broadcast.sendLogoutSignal();

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.logoutRedirect();
  } else {
    // handle logout when user comes from Admin 1 (no MSAL instance)
    window.location = getLogoutRedirectUri();
  }
}

// Clears MSAL's own "interaction in progress" flag, which can get stuck set if a user navigates away mid-redirect (e.g. clicking "Sign up now" mid-login) and blocks every later redirect attempt.
function clearStaleInteractionFlag() {
  sessionStorage.removeItem("msal.interaction.status");
}

export function initiateLogin() {
  clearStaleInteractionFlag();
  const request = getSsoSilentRequestBody();

  return msalInstance
    .ssoSilent(request)
    .then(async (response) => {
      await handleAuthenticatedUser(response);
    })
    .catch((error) => {
      msalInstance.loginRedirect({
        scopes: b2cPolicies.scopes,
      });
    });
}

// Used by "Get Started" entry points: sends brand-new users straight to the sign-up policy instead of the combined sign-in page.
export function initiateSignup(referralCid) {
  clearStaleInteractionFlag();
  sessionStorage.setItem("authFlowOrigin", "signup");
  // Lets NewAccountWizard show its one-time welcome popup only for this brand-new account, not
  // on every later visit to the wizard - set here so it trips for every entry point that starts
  // a signup, not just the ones that happen to redirect through a particular route.
  sessionStorage.setItem("justSignedUp", "true");
  if (referralCid) {
    sessionStorage.setItem("referralCid", referralCid);
  }
  return msalInstance.loginRedirect({
    scopes: b2cPolicies.scopes,
    authority: b2cPolicies.signupAuthority,
  });
}

// Starts a fresh request against the password-reset policy after the combined sign-in policy aborts with AADB2C90118 (user clicked "Forgot your password?").
export function initiatePasswordReset() {
  clearStaleInteractionFlag();
  return msalInstance.loginRedirect({
    scopes: b2cPolicies.scopes,
    authority: b2cPolicies.passwordResetAuthority,
  });
}

export function isForgotPasswordError(error) {
  return Boolean(error?.errorMessage?.includes("AADB2C90118"));
}

export async function extendSession() {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 0) {
    return;
  }

  const request = {
    account: accounts[0],
    scopes: b2cPolicies.scopes,
  };

  try {
    const accessToken = await msalInstance.acquireTokenSilent(request);
    handleAuthenticatedUser(accessToken);
    return true;
  } catch (error) {
    return error;
  }
}

async function handleAuthenticatedUser(response) {
  const userId = response.account.idTokenClaims.extension_efundid;
  const accessToken = response.accessToken;
  const currentUserId = store.getters["auth/getUserId"];

  // Only reset state if it's a different user or no current user
  const needsPermissionRefresh = !currentUserId || currentUserId !== userId;

  if (needsPermissionRefresh) {
    store.dispatch("auth/resetState");
  }

  // Always update authentication with token.
  // Don't trust userId from token due to issues with initial login.
  // Gets filled in during permissions fetch
  store.dispatch("auth/setAuthentication", {
    accessToken: accessToken,
  });

  return auth.getPermissions(accessToken);
}

function getSsoSilentRequestBody() {
  const userEmail = store.getters["auth/getUserEmail"];

  return userEmail
    ? {
        scopes: b2cPolicies.scopes,
        loginHint: userEmail,
      }
    : {
        scopes: b2cPolicies.scopes,
      };
}
